import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { type QueueStatus, useOfflineQueue } from "@/hooks/useOfflineQueue";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import type { DatasetId, DatasetScope } from "@/lib/offline";
import { prepareDataset } from "@/lib/offline";
import type { QueueItem } from "@/lib/offlineQueue";

export type SyncState = "online" | "offline" | "queued" | "syncing";

export interface SyncStatusContextValue {
	syncState: SyncState;
	online: boolean;
	queueDepth: number;
	queueItems: QueueItem[];
	queueStatus: QueueStatus;
	queueReplayingIds: ReadonlySet<string>;
	replayItem: (id: string) => Promise<void>;
	replayAll: () => Promise<void>;
	discardItem: (id: string) => Promise<void>;
	refreshQueue: () => Promise<void>;
	/** Estimated storage usage for the origin (bytes). */
	storageEstimate: { usage: number; quota: number } | null;
	/** Prepare a specific dataset for offline use. */
	prepare: (
		datasetId: DatasetId,
		scope: DatasetScope,
	) => Promise<{ ok: boolean; overCap?: boolean; error?: string }>;
	/** Prepare all provided dataset scopes sequentially, reporting per-scope outcomes. */
	prepareAll: (
		datasetId: DatasetId,
		scopes: DatasetScope[],
	) => Promise<{
		ok: boolean;
		failures: { scope: DatasetScope; error?: string; overCap?: boolean }[];
	}>;
}

const SyncStatusContext = createContext<SyncStatusContextValue | null>(null);

export function SyncStatusProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const online = useOnlineStatus();
	const queue = useOfflineQueue();
	const queryClient = useQueryClient();
	const [storageEstimate, setStorageEstimate] = useState<{
		usage: number;
		quota: number;
	} | null>(null);

	// Refresh storage estimate periodically and on mount.
	useEffect(() => {
		async function refreshStorage() {
			try {
				const estimate = await navigator.storage?.estimate?.();
				if (estimate) {
					setStorageEstimate({
						usage: estimate.usage ?? 0,
						quota: estimate.quota ?? 0,
					});
				}
			} catch {
				// non-fatal
			}
		}
		refreshStorage();
	}, []);

	const syncState: SyncState = useMemo(() => {
		if (queue.status === "replaying") return "syncing";
		if (queue.depth > 0) return "queued"; // pending items trump online/offline label
		if (!online) return "offline";
		return "online";
	}, [online, queue.depth, queue.status]);

	const refreshQueue = useCallback(() => queue.refresh(), [queue]);

	const prepare = useCallback(
		async (datasetId: DatasetId, scope: DatasetScope) => {
			return prepareDataset(queryClient, datasetId, scope);
		},
		[queryClient],
	);

	const prepareAll = useCallback(
		async (datasetId: DatasetId, scopes: DatasetScope[]) => {
			const failures: {
				scope: DatasetScope;
				error?: string;
				overCap?: boolean;
			}[] = [];
			for (const scope of scopes) {
				const result = await prepareDataset(queryClient, datasetId, scope);
				if (!result.ok) {
					failures.push({
						scope,
						error: result.error,
						overCap: result.overCap,
					});
				}
			}
			return { ok: failures.length === 0, failures };
		},
		[queryClient],
	);

	const value = useMemo<SyncStatusContextValue>(
		() => ({
			syncState,
			online,
			queueDepth: queue.depth,
			queueItems: queue.items,
			queueStatus: queue.status,
			queueReplayingIds: queue.replayingIds,
			replayItem: queue.replayItem,
			replayAll: queue.replayAll,
			discardItem: queue.discardItem,
			refreshQueue,
			storageEstimate,
			prepare,
			prepareAll,
		}),
		[
			syncState,
			online,
			queue,
			refreshQueue,
			storageEstimate,
			prepare,
			prepareAll,
		],
	);

	return (
		<SyncStatusContext.Provider value={value}>
			{children}
		</SyncStatusContext.Provider>
	);
}

export function useSyncStatus(): SyncStatusContextValue {
	const ctx = useContext(SyncStatusContext);
	if (!ctx)
		throw new Error("useSyncStatus must be used inside SyncStatusProvider");
	return ctx;
}
